/* PLTX2 fullscreen mobile menu drawer.
   When the section is enabled, intercepts clicks on the ShrineTheme hamburger
   (document capture phase — fires before the theme's own <details> toggle)
   and opens this drawer instead. Disable the section to restore the old drawer.

   Perf invariants (paired with pltx2-menu-drawer.css):
   - Panel stays painted off-screen (warm compositor layer) — `inert` blocks
     focus/AT while closed instead of visibility:hidden.
   - The html/body scroll lock reflows the whole page, so it is applied only
     AFTER the open slide completes and removed only after the close completes. */
(() => {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const SLIDE_MS = 520; // fallback if transitionend never fires

  class Pltx2MenuDrawer extends HTMLElement {
    #onDocClick = null;
    #onKeydown = null;
    #trigger = null;
    #lockTimer = null;

    connectedCallback() {
      this.panel = this.querySelector('.pltx2-mdrawer__panel');
      this.closeBtn = this.querySelector('.pltx2-mdrawer__close');
      this.setAttribute('inert', '');

      this.closeBtn?.addEventListener('click', () => this.close());

      // Any link tap closes the drawer (navigation proceeds normally)
      this.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) this.close();
      });

      this.#onDocClick = (event) => {
        const summary = event.target.closest?.('header-drawer summary.header__icon--menu');
        if (!summary) return;
        event.preventDefault();
        event.stopPropagation();
        this.open(summary);
      };
      document.addEventListener('click', this.#onDocClick, true);

      this.#onKeydown = (event) => {
        if (!this.classList.contains('is-open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusables = [...this.querySelectorAll(FOCUSABLE)];
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this.#onKeydown);

      // Theme editor: selecting the section previews the open drawer
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) this.open(null);
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) this.close();
        });
      }
    }

    disconnectedCallback() {
      if (this.#onDocClick) document.removeEventListener('click', this.#onDocClick, true);
      if (this.#onKeydown) document.removeEventListener('keydown', this.#onKeydown);
      this.#cancelPendingLock();
      document.documentElement.classList.remove('pltx2-mdrawer-lock');
      document.documentElement.classList.remove('pltx2-mdrawer-open');
    }

    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.#trigger = trigger || null;
      this.#trigger?.setAttribute('aria-expanded', 'true');
      this.removeAttribute('inert');
      this.classList.add('is-open');
      // immediate (unlike the deferred scroll lock): hides floating widgets
      // (WhatsApp bubble, a11y tool) that would overlay the fullscreen menu
      document.documentElement.classList.add('pltx2-mdrawer-open');
      this.setAttribute('aria-hidden', 'false');
      // Lock page scroll only once the slide is done — the reflow would hitch the animation
      this.#afterSlide(() => {
        if (this.classList.contains('is-open')) {
          document.documentElement.classList.add('pltx2-mdrawer-lock');
        }
      });
      this.closeBtn?.focus({ preventScroll: true });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.#cancelPendingLock();
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.#afterSlide(() => {
        if (!this.classList.contains('is-open')) {
          document.documentElement.classList.remove('pltx2-mdrawer-lock');
          // floaters come back only after the slide-out finishes
          document.documentElement.classList.remove('pltx2-mdrawer-open');
          this.setAttribute('inert', '');
        }
      });
      this.#trigger?.setAttribute('aria-expanded', 'false');
      this.#trigger?.focus({ preventScroll: true });
      this.#trigger = null;
    }

    #afterSlide(callback) {
      this.#cancelPendingLock();
      let done = false;
      const run = () => {
        if (done) return;
        done = true;
        this.panel?.removeEventListener('transitionend', onEnd);
        callback();
      };
      const onEnd = (event) => {
        if (event.target === this.panel && event.propertyName === 'transform') run();
      };
      this.panel?.addEventListener('transitionend', onEnd);
      this.#lockTimer = setTimeout(run, SLIDE_MS);
    }

    #cancelPendingLock() {
      if (this.#lockTimer) {
        clearTimeout(this.#lockTimer);
        this.#lockTimer = null;
      }
    }
  }

  if (!customElements.get('pltx2-menu-drawer')) {
    customElements.define('pltx2-menu-drawer', Pltx2MenuDrawer);
  }
})();
